/// <amd-module name="Search/WorkItem/Flux/Sources/TelemetryWriter" />

import { IVssPerformanceService } from "VSS/Platform/Performance";
import { IVssPageContext } from "VSS/Platform/Context";
import {
    ITelemetryWriter,
    publishTelemetryEvent,
    SearchPerfTelemetryAdditionalData
} from "Search/Shared/Base/Telemetry";
import { CustomerIntelligenceConstants } from "Search/WorkItem/Constants";
import { SEARCH_AREA } from "Search/Shared/CustomerIntelligenceConstants";
import { HubTelemetryWriter } from "Search/Shared/Base/Scenario";
import { ExcludeTTISettingKey } from "Search/Shared/Constants";
import { getSetting } from "Search/Shared/Base/LocalSettingsService";

/**
 * Writes telemetry events for work item search page.
 */
export class TelemetryWriter implements ITelemetryWriter {
    private ttiMarked: boolean;
    private previewScenario: string;
    private _excludeMarkingTTI: boolean;

    constructor(
        private isTabSwitch: boolean,
        private pageContext: IVssPageContext,
        private ttiMarker: HubTelemetryWriter) {
        this.ttiMarked = false;
        this._excludeMarkingTTI = getSetting<boolean>(pageContext, ExcludeTTISettingKey, false);
    }

    public publish = (feature: string, extraProperties: { [key: string]: any } = {}): void => {
        publishTelemetryEvent(this.pageContext, feature, CustomerIntelligenceConstants.EntityName, extraProperties);
    }

    public dispose = (): void => {
    }

    public notifySearchStarted = (): void => {
        if (this.ttiMarked) {
            this.service.startScenario(CustomerIntelligenceConstants.SubsequentSearchScenarioName, false, undefined, { isSubSequentSearchScenario: true });
            this.service.addSplitTiming("searchStarted", CustomerIntelligenceConstants.SubsequentSearchScenarioName);
            this.previewScenario = "";
        } else {
            this.previewScenario = this.isTabSwitch
                ? CustomerIntelligenceConstants.PreviewOnTabSwitchScenario
                : CustomerIntelligenceConstants.RedirectedPreviewScenario;
            this.service.startScenario(this.previewScenario, false);
            this.service.addSplitTiming("searchStarted", this.previewScenario);
        }
    }

    public notifySearchFailed = (): void => {
        const markScenario = this.isTabSwitch ? this.ttiMarker.markTabSwitch : this.ttiMarker.markTTI;
        const scenario = this.isTabSwitch ? CustomerIntelligenceConstants.EntityName : CustomerIntelligenceConstants.TTIScenarioName;
        if (!this.ttiMarked) {
            markScenario(scenario, { isAborted: true, isLWP: true });
            this.ttiMarked = true;
        } else {
            this.service.endScenario(SEARCH_AREA, CustomerIntelligenceConstants.SubsequentSearchScenarioName, false, undefined, { isAborted: true, isLWP: true });
        }
    }

    public notifyResultsRendered = (data: SearchPerfTelemetryAdditionalData): void => {
        if (!this.ttiMarked) {
            const properties = { ...data, isLWP: true, isLandingPage: !this.isTabSwitch, isExcludedFromTTI: this._excludeMarkingTTI };
            const scenario = this.isTabSwitch
                ? CustomerIntelligenceConstants.EntityName
                : CustomerIntelligenceConstants.TTIScenarioName;
            this.isTabSwitch
                ? this.ttiMarker.markTabSwitch(scenario, properties)
                : this.ttiMarker.markTTI(scenario, properties, this._excludeMarkingTTI);
            this.ttiMarked = true;
        } else {
            this.service.addSplitTiming("resultsLoaded", CustomerIntelligenceConstants.SubsequentSearchScenarioName);
            this.service.endScenario(
                SEARCH_AREA,
                CustomerIntelligenceConstants.SubsequentSearchScenarioName,
                !this._excludeMarkingTTI,
                undefined,
                { ...data, isLWP: true, isExcludedFromTTI: this._excludeMarkingTTI });
        }
    }

    public notifyPreviewRendered = (isSuccess: boolean): void => {
        if (!this.previewScenario) {
            return;
        }

        if (isSuccess) {
            this.service.addSplitTiming("previewRendered", this.previewScenario);
            this.service.endScenario(SEARCH_AREA, this.previewScenario, false, undefined, { isLWP: true });
        } else {
            this.service.endScenario(SEARCH_AREA, this.previewScenario, false, undefined, { isAborted: true, isLWP: true });
        }
    }

    private get service(): IVssPerformanceService {
        return this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
    }
}
